import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import axios from "axios";
import { toast } from "react-toastify";
import CartIcon from "../../components/common/CartIcon";

const apiUrl = import.meta.env.VITE_APP_API;

function BookStore() {
    const [categories, setCategories] = useState([]);
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [booksByCategory, setBooksByCategory] = useState({});

    useEffect(() => {
        fetchCategoriesAndBooks();
    }, []);

    const fetchCategoriesAndBooks = async () => {
        try {
            setLoading(true);
            setError(null);

            const categoriesResponse = await axios.get(`${apiUrl}/api/bookstore/api/categories/`);
            const categoriesData = categoriesResponse.data.results || categoriesResponse.data;
            setCategories(categoriesData);

            const booksResponse = await axios.get(`${apiUrl}/api/bookstore/api/books/`);
            const booksData = booksResponse.data.results || booksResponse.data;
            setBooks(booksData);

            const groupedBooks = {};
            categoriesData.forEach(category => {
                const categoryBooks = booksData.filter(book => book.category === category.id);
                if (categoryBooks.length > 0) {
                    groupedBooks[category.name] = categoryBooks.map(book => ({
                        id: book.id,
                        title: book.name,
                        price: parseFloat(book.price),
                        discount: parseFloat(book.discount),
                        image: book.image || null,
                        author: book.author,
                        publisher: book.publisher,
                        description: book.description,
                        total_amount: book.total_amount
                    }));
                }
            });

            setBooksByCategory(groupedBooks);
        } catch (error) {
            console.error('Error fetching books and categories:', error);
            setError('Failed to load books. Please try again later.');
            toast.error('Failed to load books');
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        fetchCategoriesAndBooks();
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 mb-4"></div>
                    <p className="text-gray-600 text-lg font-medium">Loading books...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="text-center">
                    <div className="text-red-500 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Books</h3>
                    <p className="text-gray-500 mb-4">{error}</p>
                    <button
                        onClick={handleRefresh}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 text-sm font-medium"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (Object.keys(booksByCategory).length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="text-center">
                    <div className="text-gray-500 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Books Available</h3>
                    <p className="text-gray-500 mb-4">No books have been added yet.</p>
                    <button
                        onClick={handleRefresh}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 text-sm font-medium"
                    >
                        Refresh
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8 mt-20">
            <div className="mx-20">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Book Store</h1>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={handleRefresh}
                            className="inline-flex items-center px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-all duration-200 text-sm font-medium"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Refresh
                        </button>
                        <CartIcon />
                    </div>
                </div>

                {Object.entries(booksByCategory).map(([sectionTitle, sectionBooks], idx) => (
                    <div key={idx} className="bg-white rounded-2xl shadow-xl p-6 mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 text-center mb-6">{sectionTitle}</h2>
                        <div className="relative">
                            <Swiper
                                modules={[Navigation, Pagination]}
                                navigation
                                pagination={{ clickable: true }}
                                spaceBetween={20}
                                slidesPerView={1}
                                breakpoints={{
                                    640: { slidesPerView: 2 },
                                    768: { slidesPerView: 3 },
                                    1024: { slidesPerView: 4 },
                                }}
                            >
                                {sectionBooks.map((book, i) => (
                                    <SwiperSlide key={book.id || i}>
                                        <div className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-lg transition-all duration-200 h-full flex flex-col">
                                            <div className="relative">
                                                {book.image ? (
                                                    <img
                                                        src={book.image}
                                                        alt={book.title}
                                                        className="w-full h-56 object-cover rounded-lg mb-3"
                                                        onError={(e) => {
                                                            e.target.src = '/images/book-placeholder.jpg';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="w-full h-56 bg-gray-100 rounded-lg mb-3 flex items-center justify-center">
                                                        <span className="text-gray-400 text-sm">No Image</span>
                                                    </div>
                                                )}
                                                <div className="absolute top-2 right-2">
                                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                        book.total_amount === 0
                                                            ? 'bg-red-100 text-red-800'
                                                            : book.total_amount < 5
                                                                ? 'bg-yellow-100 text-yellow-800'
                                                                : 'bg-green-100 text-green-800'
                                                    }`}>
                                                        {book.total_amount === 0 ? 'Out of Stock' : `${book.total_amount} left`}
                                                    </span>
                                                </div>
                                            </div>
                                            <h3 className="text-sm font-semibold text-gray-900 flex-grow mb-2">{book.title}</h3>
                                            <div className="text-xs text-gray-600 mb-2">
                                                <p>By: {book.author}</p>
                                                <p>Publisher: {book.publisher}</p>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    {book.discount > 0 ? (
                                                        <div>
                                                            <span className="text-gray-400 line-through text-sm">
                                                                Tk. {book.price.toFixed(2)}
                                                            </span>
                                                            <p className="text-pink-600 font-semibold text-base">
                                                                Tk. {(book.price - book.discount).toFixed(2)}
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <p className="text-pink-600 font-semibold text-base">
                                                            Tk. {book.price.toFixed(2)}
                                                        </p>
                                                    )}
                                                </div>
                                                <Link
                                                    to={`/book-store/${book.id}`}
                                                    className="px-3 py-1 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200"
                                                >
                                                    Details
                                                </Link>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default BookStore;